<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯加好友行銷' : '新增加好友行銷' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button
          v-if="!isEdit || activeTab === 'settings'"
          type="button"
          class="btn btn-primary"
          :disabled="saving"
          @click="handleSubmit"
        >
          {{ saving ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isEdit" class="detail-tabs">
      <button
        v-for="tab in detailTabs"
        :key="tab.key"
        type="button"
        class="tab-btn"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <i :class="tab.icon"></i>
        {{ tab.label }}
      </button>
    </div>

    <div v-show="!isEdit || activeTab === 'settings'" class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本設定</h3>
          <div class="form-row">
            <div class="form-group">
              <label class="required">活動代碼</label>
              <input
                v-model="form.campaignCode"
                type="text"
                class="form-control"
                :disabled="isEdit"
                placeholder="已自動產生，可自行修改"
              />
              <p v-if="!isEdit" class="hint field-hint">新增時會自動帶入唯一代碼，無需手動填寫。</p>
            </div>
            <div class="form-group">
              <label class="required">活動名稱</label>
              <input v-model="form.campaignName" type="text" class="form-control" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="required">LINE 官方帳號</label>
              <select v-model="form.messagingChannelId" class="form-control">
                <option value="">請選擇</option>
                <option v-for="c in lineConfigs" :key="c.id" :value="lineChannelId(c)">
                  {{ c.configName || lineChannelId(c) || c.id }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>排序（數字小優先）</label>
              <input v-model.number="form.sortOrder" type="number" class="form-control" />
            </div>
            <div class="form-group">
              <label>狀態</label>
              <div class="toggle-row">
                <label class="toggle-switch">
                  <input v-model="form.isActive" type="checkbox" />
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-status" :class="form.isActive ? 'on' : 'off'">
                  {{ form.isActive ? '已啟用' : '已停用' }}
                </span>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>開始時間</label>
              <input v-model="form.startDate" type="datetime-local" class="form-control" />
            </div>
            <div class="form-group">
              <label>結束時間</label>
              <input v-model="form.endDate" type="datetime-local" class="form-control" />
            </div>
          </div>
          <p class="hint period-hint">
            未填開始時間表示隨時開始；未填結束時間表示永久有效。若兩者皆填，開始須不晚於結束。
          </p>
        </div>

        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">加好友後動作（依序執行）</h3>
            <button type="button" class="btn btn-primary" @click="addAction">
              <i class="ri-add-line"></i>
              新增動作
            </button>
          </div>
          <p class="hint">
            發券需使用者已綁定會員；若尚未綁定將略過該步驟。貼標籤同樣需已綁定會員。抽獎連結與「發送推播」會以 LINE 推播發送（推播請選 LINE 訊息範本）。點數為會員專屬獎勵，加好友行銷不提供發點。
            同一 LINE 官方帳號在<strong>活動期間重疊</strong>的情況下，同時只能有一個<strong>啟用中</strong>的活動（可先停用其他活動或調整期間）。
          </p>

          <div
            v-for="(act, idx) in form.actions"
            :key="act.uid"
            class="reward-card"
            :class="{ 'is-collapsed': act._collapsed }"
          >
            <div class="reward-card-header" @click="toggleActionCollapse(act)">
              <button
                type="button"
                class="collapse-btn"
                :title="act._collapsed ? '展開' : '收合'"
                @click.stop="toggleActionCollapse(act)"
              >
                <i :class="act._collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'"></i>
              </button>
              <div class="reward-card-heading">
                <strong>動作 {{ idx + 1 }}</strong>
                <span v-if="act._collapsed" class="reward-card-summary">{{ actionSummary(act) }}</span>
              </div>
              <button type="button" class="btn btn-sm btn-danger" @click.stop="removeAction(idx)">刪除</button>
            </div>
            <div v-show="!act._collapsed" class="reward-card-body">
            <div class="form-row">
              <div class="form-group">
                <label>類型</label>
                <select v-model="act.type" class="form-control">
                  <option value="coupon">發優惠券</option>
                  <option value="lottery">發送抽獎連結（推播文字）</option>
                  <option value="message">發送推播（LINE 訊息範本）</option>
                  <option value="tag">貼標籤</option>
                  <option v-if="isWixtarClient || act.type === 'wixtar_ticket'" value="wixtar_ticket">Wixtar 票券</option>
                </select>
              </div>
              <div class="form-group">
                <label>啟用</label>
                <select v-model="act.enabled" class="form-control">
                  <option :value="true">是</option>
                  <option :value="false">否</option>
                </select>
              </div>
            </div>

            <div v-if="act.type === 'coupon'" class="form-group full-width">
              <label>優惠券範本 <span class="required">*</span></label>
              <CouponTemplateMultiSelector
                v-model="act.templateIds"
                :templates="couponTemplates"
                :loading="couponTemplatesLoading"
                hint="可選擇多個優惠券模板，加好友後會發送所有選中的優惠券（需已綁定會員）"
              />
            </div>

            <div v-else-if="act.type === 'wixtar_ticket'" class="form-group full-width">
              <label>Wixtar 票券 <span class="required">*</span></label>
              <WixtarTicketMultiSelect
                :model-value="act.wixtarTicketIds"
                :tickets="wixtarTickets"
                :loading="wixtarTicketsLoading"
                @update:model-value="act.wixtarTicketIds = $event"
              />
              <p class="form-hint">可選擇多張票券，加好友後發送給已綁定 Wixtar 的會員。</p>
            </div>

            <div v-else-if="act.type === 'lottery'" class="form-row">
              <div class="form-group">
                <label>抽獎活動</label>
                <select v-model="act.lotteryConfigId" class="form-control">
                  <option value="">請選擇</option>
                  <option v-for="l in lotteryConfigs" :key="l.id" :value="l.id">
                    {{ l.configName || l.name || l.id }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>開頭文案</label>
                <input v-model="act.introText" type="text" class="form-control" placeholder="邀請您參加抽獎：" />
              </div>
            </div>

            <div v-else-if="act.type === 'message'" class="form-group full-width line-msg-picker-block">
              <label>LINE 訊息範本</label>
              <LineMsgTemplatePicker
                :key="`${act.uid}-line-msg`"
                audience-filter="personal"
                :model-value="act.lineMsgTemplate"
                :selected-id="act.lineMsgTemplate?.id || null"
                @update:model-value="(t) => onMessageTemplateChange(act, t)"
              />
              <div class="picker-goto-link">
                <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
                  <i class="ri-add-line"></i>
                  新增 LINE 訊息範本
                </router-link>
              </div>
            </div>

            <div v-else-if="act.type === 'tag'" class="form-group full-width">
              <label>要貼上的標籤</label>
              <TagSelectorMultiple v-model="act.tagIds" label="" hint="加好友後自動為會員貼上（需已綁定會員）" />
            </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">限制條件</h3>
          <div class="form-row">
            <div class="form-group">
              <label>每位 LINE 用戶僅限一次</label>
              <select v-model="form.limits.onePerUser" class="form-control">
                <option :value="true">是</option>
                <option :value="false">否</option>
              </select>
            </div>
            <div class="form-group">
              <label>總發放上限（空白表示不限）</label>
              <input v-model.number="form.limits.totalLimit" type="number" min="0" class="form-control" placeholder="不限" />
            </div>
          </div>
        </div>

      </form>
    </div>

    <div v-if="isEdit && activeTab === 'logs'" class="tab-panel">
      <FollowMarketingLogsPanel :campaign-id="route.params.id" />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { followMarketingCampaignService } from '@/api/modules/followMarketingCampaigns.js';
import { lineService } from '@/api/modules/line.js';
import { lotteryService } from '@/api/modules/lottery.js';
import { couponTemplateService } from '@/api/modules/couponTemplates.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import CouponTemplateMultiSelector from '@/components/CouponTemplateMultiSelector.vue';
import FollowMarketingLogsPanel from '@/components/follow-marketing/FollowMarketingLogsPanel.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import { clientService } from '@/api/modules/clients.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';
import { showError, showSuccess } from '@/utils/swal.js';

function newUid() {
  return `a_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** 產生預設活動代碼（後端 maxLength 50） */
function suggestFollowCampaignCode() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `FOLLOW_${y}${m}${day}_${rand}`;
}

function emptyAction(type = 'message') {
  return {
    uid: newUid(),
    type,
    enabled: true,
    templateIds: [],
    lotteryConfigId: '',
    introText: '邀請您參加抽獎：',
    lineMsgTemplate: null,
    tagIds: [],
    wixtarTicketIds: [],
    /** 舊資料僅存純文字推播時保留，新建立請用 lineMsgTemplate */
    text: '',
    /** UI：收合動作表單 */
    _collapsed: false
  };
}

export default {
  name: 'FollowMarketingCampaignForm',
  components: {
    LineMsgTemplatePicker,
    TagSelectorMultiple,
    CouponTemplateMultiSelector,
    FollowMarketingLogsPanel,
    WixtarTicketMultiSelect
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isEdit = computed(() => !!route.params.id);
    const detailTabs = [
      { key: 'settings', label: '活動設定', icon: 'ri-settings-3-line' },
      { key: 'logs', label: '參加紀錄', icon: 'ri-history-line' }
    ];
    const resolveTab = (tab) => (['settings', 'logs'].includes(tab) ? tab : 'settings');
    const activeTab = ref(resolveTab(route.query.tab));

    const lineConfigs = ref([]);
    const lotteryConfigs = ref([]);
    const couponTemplates = ref([]);
    const couponTemplatesLoading = ref(false);
    const isWixtarClient = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);
    const saving = ref(false);
    const originalLimits = ref(null);

    const form = reactive({
      campaignCode: '',
      campaignName: '',
      messagingChannelId: '',
      sortOrder: 0,
      isActive: true,
      startDate: '',
      endDate: '',
      actions: [emptyAction('message')],
      limits: {
        onePerUser: true,
        totalLimit: null
      }
    });

    /** LINE 列表 API 的 Channel ID 在 messagingApi.channelId（無頂層 messagingChannelId） */
    const lineChannelId = (c) =>
      (c && (c.messagingChannelId || c.messagingApi?.channelId)) || '';

    const unwrapList = (res) => {
      if (!res) return [];
      if (res.success && res.data) {
        if (Array.isArray(res.data.items)) return res.data.items;
        if (Array.isArray(res.data)) return res.data;
      }
      if (Array.isArray(res.items)) return res.items;
      if (Array.isArray(res.data?.items)) return res.data.items;
      return [];
    };

    const loadRefs = async () => {
      try {
        couponTemplatesLoading.value = true;
        const [lineRes, lotRes, coupRes] = await Promise.all([
          lineService.getConfigs({ page: 1, pageSize: 100 }),
          lotteryService.getConfigs({ page: 1, pageSize: 100, isActive: true }),
          couponTemplateService.getCouponTemplates({ page: 1, pageSize: 200, isActive: true })
        ]);
        lineConfigs.value = unwrapList(lineRes);
        lotteryConfigs.value = unwrapList(lotRes);
        couponTemplates.value = unwrapList(coupRes);
        if (lineConfigs.value.length === 1 && !form.messagingChannelId) {
          form.messagingChannelId = lineChannelId(lineConfigs.value[0]);
        }
      } catch (e) {
        console.warn(e);
      } finally {
        couponTemplatesLoading.value = false;
      }
    };

    const toLocal = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const loadCampaign = async () => {
      if (!isEdit.value) return;
      try {
        const res = await followMarketingCampaignService.getCampaign(route.params.id);
        const c = res.data?.data ?? res.data;
        form.campaignCode = c.campaignCode;
        form.campaignName = c.campaignName;
        form.messagingChannelId = c.messagingChannelId || '';
        form.sortOrder = c.sortOrder ?? 0;
        form.isActive = c.isActive !== false;
        form.startDate = toLocal(c.startDate);
        form.endDate = toLocal(c.endDate);
        form.limits.onePerUser = c.limits?.onePerUser !== false;
        form.limits.totalLimit = c.limits?.totalLimit ?? null;
        originalLimits.value = c.limits ? { ...c.limits } : null;

        const rawActions = c.rewards?.actions;
        if (Array.isArray(rawActions) && rawActions.length) {
          const mapped = [];
          for (const a of rawActions) {
            if (a.type === 'points') continue;
            const base = emptyAction(a.type || 'message');
            base.uid = newUid();
            base.type = a.type || 'message';
            base.enabled = a.enabled !== false;
            if (a.type === 'coupon') {
              base.templateIds = [...(a.templateIds || a.couponTemplateIds || [])];
            }
            if (a.type === 'lottery') {
              base.lotteryConfigId = a.lotteryConfigId || '';
              base.introText = a.introText || base.introText;
            }
            if (a.type === 'message') {
              const tid = a.lineMsgTemplateId || a.templateId;
              if (tid) {
                try {
                  const tr = await lineMsgTemplateService.getById(tid);
                  const t = tr.data?.data ?? tr.data;
                  base.lineMsgTemplate = t || null;
                } catch {
                  base.lineMsgTemplate = null;
                }
              }
              base.text = a.text || '';
            }
            if (a.type === 'tag') {
              base.tagIds = [...(a.tagIds || [])];
            }
            if (a.type === 'wixtar_ticket') {
              base.wixtarTicketIds = [...(a.wixtarTicketIds || a.ticketIds || [])];
            }
            base._collapsed = mapped.length > 0;
            mapped.push(base);
          }
          if (mapped.length) {
            form.actions = mapped;
          }
        }
      } catch (e) {
        showError(e.response?.data?.message || e.message || '載入失敗');
      }
    };

    const optionalLocalToIso = (local) => {
      if (!local || !String(local).trim()) return null;
      const d = new Date(local);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    };

    const buildPayload = () => {
      const actions = form.actions
        .filter((a) => a.enabled !== false)
        .map((a) => {
          if (a.type === 'coupon') {
            return { type: 'coupon', enabled: true, templateIds: a.templateIds || [] };
          }
          if (a.type === 'lottery') {
            return {
              type: 'lottery',
              enabled: true,
              lotteryConfigId: a.lotteryConfigId,
              introText: a.introText
            };
          }
          if (a.type === 'message') {
            const tid = a.lineMsgTemplate?.id;
            if (tid) {
              return { type: 'message', enabled: true, lineMsgTemplateId: tid };
            }
            const legacy = (a.text || '').trim();
            if (legacy) {
              return { type: 'message', enabled: true, text: legacy };
            }
            return { type: 'message', enabled: true };
          }
          if (a.type === 'tag') {
            return { type: 'tag', enabled: true, tagIds: a.tagIds || [] };
          }
          if (a.type === 'wixtar_ticket') {
            if (memberSystemLoaded.value && !isWixtarClient.value) return null;
            return { type: 'wixtar_ticket', enabled: true, wixtarTicketIds: a.wixtarTicketIds || [] };
          }
          return null;
        })
        .filter(Boolean);

      return {
        campaignCode: form.campaignCode.trim(),
        campaignName: form.campaignName.trim(),
        messagingChannelId: form.messagingChannelId,
        sortOrder: form.sortOrder,
        isActive: form.isActive,
        startDate: optionalLocalToIso(form.startDate),
        endDate: optionalLocalToIso(form.endDate),
        rewards: { actions },
        limits: {
          onePerUser: form.limits.onePerUser,
          dailyLimit: originalLimits.value?.dailyLimit ?? null,
          totalLimit: form.limits.totalLimit || null,
          currentDailyCount: originalLimits.value?.currentDailyCount ?? 0,
          currentTotalCount: originalLimits.value?.currentTotalCount ?? 0
        }
      };
    };

    const handleSubmit = async () => {
      if (!form.campaignCode.trim() || !form.campaignName.trim()) {
        showError('請填寫活動代碼與名稱');
        return;
      }
      if (!form.messagingChannelId) {
        showError('請選擇 LINE 官方帳號');
        return;
      }
      if (form.startDate && form.endDate && new Date(form.startDate) > new Date(form.endDate)) {
        showError('開始時間不可晚於結束時間');
        return;
      }
      const payload = buildPayload();
      if (!payload.rewards.actions.length) {
        showError('請至少新增一個啟用中的動作');
        return;
      }
      const msgWithoutTpl = form.actions.find(
        (a) =>
          a.enabled !== false &&
          a.type === 'message' &&
          !a.lineMsgTemplate?.id &&
          !(a.text || '').trim()
      );
      if (msgWithoutTpl) {
        showError('請為「發送推播」動作選擇 LINE 訊息範本');
        return;
      }
      const tagWithoutIds = form.actions.find(
        (a) => a.enabled !== false && a.type === 'tag' && !(a.tagIds || []).length
      );
      if (tagWithoutIds) {
        showError('請為「貼標籤」動作至少選擇一個標籤');
        return;
      }
      const couponWithoutTpl = form.actions.find(
        (a) =>
          a.enabled !== false &&
          a.type === 'coupon' &&
          !(a.templateIds || []).length
      );
      if (couponWithoutTpl) {
        showError('請為「發優惠券」動作至少選擇一個優惠券範本');
        return;
      }
      const wixtarWithoutTickets = form.actions.find(
        (a) => a.enabled !== false && a.type === 'wixtar_ticket' && !(a.wixtarTicketIds || []).length
      );
      if (wixtarWithoutTickets) {
        showError('請為「Wixtar 票券」動作至少選擇一張票券');
        return;
      }

      saving.value = true;
      try {
        if (isEdit.value) {
          await followMarketingCampaignService.updateCampaign(route.params.id, payload);
          showSuccess('更新成功');
        } else {
          await followMarketingCampaignService.createCampaign(payload);
          showSuccess('建立成功');
        }
        router.push({ name: 'FollowMarketingCampaignList' });
      } catch (e) {
        showError(e.response?.data?.message || e.message || '儲存失敗');
      } finally {
        saving.value = false;
      }
    };

    const toggleActionCollapse = (act) => {
      act._collapsed = !act._collapsed;
    };

    const actionSummary = (act) => {
      const off = act.enabled === false ? ' · 停用' : '';
      if (act.type === 'coupon') {
        const n = Array.isArray(act.templateIds) ? act.templateIds.length : 0;
        return `優惠券 · 已選 ${n} 個範本${off}`;
      }
      if (act.type === 'wixtar_ticket') {
        const n = Array.isArray(act.wixtarTicketIds) ? act.wixtarTicketIds.length : 0;
        return `Wixtar 票券 · 已選 ${n} 張${off}`;
      }
      if (act.type === 'lottery') {
        const list = lotteryConfigs.value || [];
        const l = list.find((x) => x.id === act.lotteryConfigId);
        const name = l ? l.configName || l.name || act.lotteryConfigId : '未選擇';
        return `抽獎連結 · ${name}${off}`;
      }
      if (act.type === 'message') {
        const name = act.lineMsgTemplate?.name;
        if (name) return `推播 · ${name}${off}`;
        if ((act.text || '').trim()) return `推播 · 純文字${off}`;
        return `推播 · 未選範本${off}`;
      }
      if (act.type === 'tag') {
        const n = Array.isArray(act.tagIds) ? act.tagIds.length : 0;
        return `貼標籤 · ${n} 個${off}`;
      }
      return `動作${off}`;
    };

    const addAction = () => {
      form.actions.forEach((a) => {
        a._collapsed = true;
      });
      const a = emptyAction('message');
      a._collapsed = false;
      form.actions.push(a);
    };

    const removeAction = (idx) => {
      form.actions.splice(idx, 1);
      if (!form.actions.length) form.actions.push(emptyAction('message'));
    };

    const handleCancel = () => {
      router.push({ name: 'FollowMarketingCampaignList' });
    };

    const onMessageTemplateChange = (act, template) => {
      act.lineMsgTemplate = template;
    };

    const loadWixtarContext = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.success && res.data?.clientData?.memberSystemType === 'WIXTAR';
        memberSystemLoaded.value = true;
      } catch {
        memberSystemLoaded.value = false;
        return;
      }
      if (!isWixtarClient.value) return;
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (err) {
        console.error('載入 Wixtar 票券失敗:', err);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([loadRefs(), loadWixtarContext()]);
      await loadCampaign();
      if (!isEdit.value && !String(form.campaignCode || '').trim()) {
        form.campaignCode = suggestFollowCampaignCode();
      }
    });

    watch(activeTab, (tab) => {
      if (!isEdit.value) return;
      router.replace({
        name: 'EditFollowMarketingCampaign',
        params: { id: route.params.id },
        query: tab === 'settings' ? {} : { tab }
      });
    });

    return {
      isEdit,
      route,
      activeTab,
      detailTabs,
      form,
      lineConfigs,
      lotteryConfigs,
      couponTemplates,
      couponTemplatesLoading,
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading,
      saving,
      lineChannelId,
      addAction,
      removeAction,
      handleSubmit,
      handleCancel,
      onMessageTemplateChange,
      toggleActionCollapse,
      actionSummary
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.form-container {
  max-width: 960px;
}
.form-section {
  margin-bottom: 28px;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.section-title {
  font-size: 1.1rem;
  margin: 0 0 12px;
}
.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.form-group {
  flex: 1;
  min-width: 200px;
  margin-bottom: 12px;
}
.form-group.full-width {
  flex: 100%;
}
.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}
.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}
.toggle-status {
  font-size: 13px;
  font-weight: 500;
}
.toggle-status.on {
  color: #4caf50;
}
.toggle-status.off {
  color: #9ca3af;
}
.reward-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafafa;
}
.reward-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  user-select: none;
}
.reward-card:not(.is-collapsed) .reward-card-header {
  margin-bottom: 12px;
}
.collapse-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: #e5e7eb;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
}
.collapse-btn:hover {
  background: #d1d5db;
}
.reward-card-heading {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.reward-card-summary {
  font-size: 0.85rem;
  font-weight: normal;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.reward-card-body {
  padding-top: 0;
}
.hint {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 12px;
}
.period-hint {
  margin-top: -4px;
}
.field-hint {
  margin-top: 6px;
  margin-bottom: 0;
}
.line-msg-picker-block {
  min-width: 100%;
}
.picker-goto-link {
  margin-top: 8px;
  font-size: 0.9rem;
}
.picker-goto-link a {
  color: #2563eb;
  text-decoration: none;
}
.picker-goto-link a:hover {
  text-decoration: underline;
}

.detail-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  font-weight: 600;
}

.tab-panel {
  max-width: 1200px;
}
</style>
